// src/components/Navbar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Navbar({ cartCount = 0, onLoginClick }) {
  const { consumer, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const fn = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const firstName = consumer?.profile?.firstName || consumer?.firstName || consumer?.email?.split('@')[0] || '';
  const avatarUrl = consumer?.profile?.avatarUrl;
  const initial = firstName?.[0]?.toUpperCase() || '?';

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'rgba(3,10,20,0.92)', backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(0,229,204,0.08)',
      padding: '0 40px',
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        display: 'flex', alignItems: 'center', height: 60, gap: 32,
      }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {/* Shield icon */}
          <div style={{ width: 40, height: 40, flexShrink: 0, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <img
              src="/jeptags-icon.png"
              alt="JepTags"
              style={{
                width: 40, height: 40,
                borderRadius: 10,
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
                boxShadow: '0 0 16px rgba(0,229,204,0.3)',
              }}
            />
          </div>
          {/* Text */}
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <span style={{ fontSize: 19, fontWeight: 900, letterSpacing: '-0.8px', fontFamily: '"Segoe UI", system-ui, sans-serif', WebkitTextFillColor: 'initial' }}>
              <span style={{ color: '#00E5CC', WebkitTextFillColor: '#00E5CC' }}>jep</span><span style={{ color: '#ddfdd5', WebkitTextFillColor: '#ddfdd5' }}>tags</span>
            </span>
            <span style={{
              fontSize: 7, fontWeight: 600, letterSpacing: '2.5px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
              alignSelf: 'flex-end',
              marginTop: 6,
            }}>shop</span>
          </div>
        </Link>

        {/* Nav links */}
        <div style={{ display: 'flex', gap: 4, flex: 1 }}>
          {[
            { to: '/products', label: 'Products' },
            { to: '/brands', label: 'Brands' },
          ].map(({ to, label }) => (
            <Link key={to} to={to} style={{
              color: 'rgba(224,240,240,0.55)', textDecoration: 'none',
              fontSize: 14, fontWeight: 500, padding: '6px 12px', borderRadius: 8,
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.color = '#E0F0F0'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(224,240,240,0.55)'; e.currentTarget.style.background = 'transparent'; }}>
              {label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Cart */}
          <Link to="/cart" style={{ position: 'relative', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <div style={{
              padding: '7px 14px', borderRadius: 10,
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(224,240,240,0.7)', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,229,204,0.3)'; e.currentTarget.style.color = '#00E5CC'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(224,240,240,0.7)'; }}>
              🛒
              {cartCount > 0 && (
                <span style={{
                  background: '#00E5CC', color: '#030A14',
                  borderRadius: 20, fontSize: 11, fontWeight: 800,
                  padding: '1px 7px', minWidth: 18, textAlign: 'center',
                }}>
                  {cartCount}
                </span>
              )}
            </div>
          </Link>

          {/* Auth section */}
          {isLoggedIn ? (
            <div ref={dropRef} style={{ position: 'relative' }}>
              <button onClick={() => setDropdownOpen(d => !d)} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 12px 5px 6px', borderRadius: 10,
                background: dropdownOpen ? 'rgba(0,229,204,0.08)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${dropdownOpen ? 'rgba(0,229,204,0.25)' : 'rgba(255,255,255,0.08)'}`,
                cursor: 'pointer', transition: 'all 0.2s',
              }}>
                {/* Avatar */}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: avatarUrl ? 'transparent' : 'linear-gradient(135deg, #00E5CC, #22D97F)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: '#030A14', overflow: 'hidden', flexShrink: 0,
                }}>
                  {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#E0F0F0', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {firstName}
                </span>
                <span style={{ fontSize: 10, color: 'rgba(224,240,240,0.4)', transition: 'transform 0.2s', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
              </button>

              {/* Dropdown */}
              {dropdownOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                  background: '#0a1628', border: '1px solid rgba(0,229,204,0.12)',
                  borderRadius: 12, padding: 8, minWidth: 180,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  animation: 'fadeIn 0.15s ease',
                }}>
                  {/* User info */}
                  <div style={{ padding: '8px 12px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#E0F0F0' }}>{firstName}</div>
                    <div style={{ fontSize: 11, color: 'rgba(224,240,240,0.35)', marginTop: 2 }}>{consumer?.email}</div>
                    {consumer?.loyalty?.tier && (
                      <div style={{ marginTop: 6, display: 'inline-block', padding: '2px 8px', borderRadius: 20, background: 'rgba(0,229,204,0.1)', color: '#00E5CC', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {consumer.loyalty.tier} · {consumer.loyalty.points || 0} pts
                      </div>
                    )}
                  </div>

                  {[
                    { icon: '📊', label: 'My Account', to: '/account' },
                    { icon: '📦', label: 'My Orders', to: '/account', state: { tab: 'orders' } },
                  ].map(item => (
                    <Link key={item.label} to={item.to} onClick={() => setDropdownOpen(false)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px', borderRadius: 8, textDecoration: 'none',
                      color: 'rgba(224,240,240,0.7)', fontSize: 13, fontWeight: 500,
                      transition: 'all 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#E0F0F0'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(224,240,240,0.7)'; }}>
                      <span>{item.icon}</span> {item.label}
                    </Link>
                  ))}

                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 6, paddingTop: 6 }}>
                    <button onClick={handleLogout} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px', borderRadius: 8, border: 'none',
                      background: 'transparent', color: 'rgba(239,68,68,0.6)',
                      fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; e.currentTarget.style.color = '#EF4444'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(239,68,68,0.6)'; }}>
                      <span>🚪</span> Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button onClick={onLoginClick} style={{
              padding: '7px 16px', borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0,229,204,0.12), rgba(34,217,127,0.08))',
              border: '1px solid rgba(0,229,204,0.25)',
              color: '#00E5CC', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.2s', letterSpacing: '0.2px',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0,229,204,0.2), rgba(34,217,127,0.15))'; e.currentTarget.style.borderColor = 'rgba(0,229,204,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0,229,204,0.12), rgba(34,217,127,0.08))'; e.currentTarget.style.borderColor = 'rgba(0,229,204,0.25)'; }}>
              Sign In
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px) } to { opacity: 1; transform: translateY(0) } }`}</style>
    </nav>
  );
}